import { useRef, useEffect, useState } from 'react';

const DEFAULT_COLOR = '#ffffff';

const hexToRgb = hex => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [1, 1, 1];
};

const getAnchorAndDir = (origin, w, h) => {
  const outside = 0.2;
  switch (origin) {
    case 'top-left': return { anchor: [0, -outside * h], dir: [0, 1] };
    case 'top-right': return { anchor: [w, -outside * h], dir: [0, 1] };
    case 'left': return { anchor: [-outside * w, 0.5 * h], dir: [1, 0] };
    case 'right': return { anchor: [(1 + outside) * w, 0.5 * h], dir: [-1, 0] };
    case 'bottom-left': return { anchor: [0, (1 + outside) * h], dir: [0, -1] };
    case 'bottom-center': return { anchor: [0.5 * w, (1 + outside) * h], dir: [0, -1] };
    case 'bottom-right': return { anchor: [w, (1 + outside) * h], dir: [0, -1] };
    default: return { anchor: [0.5 * w, -outside * h], dir: [0, 1] };
  }
};

const LightRays = ({
  raysOrigin = 'top-center',
  raysColor = DEFAULT_COLOR,
  raysSpeed = 1,
  lightSpread = 1,
  rayLength = 2,
  pulsating = false,
  fadeDistance = 1.0,
  saturation = 1.0,
  followMouse = true,
  mouseInfluence = 0.1,
  noiseAmount = 0.0,
  distortion = 0.0,
  lightIntensity = 1.0,
  className = ''
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = useRef({ x: 0.5, y: 0.5 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !containerRef.current) return;

    const canvas = document.createElement('canvas');
    canvasRef.current = canvas;
    canvas.style.cssText = 'width:100%; height:100%; display:block;';
    containerRef.current.appendChild(canvas);

    const gl = canvas.getContext('webgl2');
    if (!gl) return;

    const vs = `#version 300 es
      in vec2 position;
      out vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }`;

    const fs = `#version 300 es
      precision highp float;
      uniform float iTime;
      uniform vec2 iResolution;
      uniform vec2 rayPos;
      uniform vec2 rayDir;
      uniform vec3 raysColor;
      uniform float raysSpeed;
      uniform float lightSpread;
      uniform float rayLength;
      uniform float pulsating;
      uniform float fadeDistance;
      uniform float saturation;
      uniform vec2 mousePos;
      uniform float mouseInfluence;
      uniform float noiseAmount;
      uniform float distortion;
      uniform float intensity;
      out vec4 fragColor;

      float noise(vec2 st) { return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123); }

      float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord, float seedA, float seedB, float speed) {
        vec2 sourceToCoord = coord - raySource;
        vec2 dirNorm = normalize(sourceToCoord);
        float cosAngle = dot(dirNorm, rayRefDirection);
        float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;
        float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));
        float distance = length(sourceToCoord);
        float maxDistance = iResolution.x * rayLength;
        float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);
        float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);
        float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;
        float strength = clamp((0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) + (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)), 0.0, 1.0);
        return strength * lengthFalloff * fadeFalloff * spreadFactor * pulse;
      }

      void main() {
        vec2 coord = vec2(gl_FragCoord.x, iResolution.y - gl_FragCoord.y);
        vec2 finalRayDir = rayDir;
        if (mouseInfluence > 0.0) {
          vec2 mouseScreenPos = mousePos * iResolution.xy;
          vec2 mDir = normalize(mouseScreenPos - rayPos);
          finalRayDir = normalize(mix(rayDir, mDir, mouseInfluence));
        }
        float r1 = rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349, 1.5 * raysSpeed);
        float r2 = rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234, 1.1 * raysSpeed);
        vec3 outCol = vec3(r1 * 0.5 + r2 * 0.4);
        if (noiseAmount > 0.0) {
          outCol *= (1.0 - noiseAmount + noiseAmount * noise(coord * 0.01 + iTime * 0.1));
        }
        float brightness = 1.0 - (coord.y / iResolution.y);
        outCol *= vec3(0.1 + brightness * 0.8, 0.3 + brightness * 0.6, 0.5 + brightness * 0.5);
        if (saturation != 1.0) {
          float gray = dot(outCol, vec3(0.299, 0.587, 0.114));
          outCol = mix(vec3(gray), outCol, saturation);
        }
        fragColor = vec4(outCol * raysColor * intensity, 1.0);
      }`;

    const createShader = (gl, type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };

    const program = gl.createProgram();
    gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(program);
    gl.useProgram(program);

    const vertices = new Float32Array([-1, -1, 3, -1, -1, 3]);
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const locs = {
      time: gl.getUniformLocation(program, 'iTime'),
      res: gl.getUniformLocation(program, 'iResolution'),
      rPos: gl.getUniformLocation(program, 'rayPos'),
      rDir: gl.getUniformLocation(program, 'rayDir'),
      rCol: gl.getUniformLocation(program, 'raysColor'),
      rSpd: gl.getUniformLocation(program, 'raysSpeed'),
      lSpr: gl.getUniformLocation(program, 'lightSpread'),
      rLen: gl.getUniformLocation(program, 'rayLength'),
      puls: gl.getUniformLocation(program, 'pulsating'),
      fade: gl.getUniformLocation(program, 'fadeDistance'),
      sat: gl.getUniformLocation(program, 'saturation'),
      mPos: gl.getUniformLocation(program, 'mousePos'),
      mInf: gl.getUniformLocation(program, 'mouseInfluence'),
      noise: gl.getUniformLocation(program, 'noiseAmount'),
      dist: gl.getUniformLocation(program, 'distortion'),
      intens: gl.getUniformLocation(program, 'intensity'),
    };

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = containerRef.current;
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(locs.res, canvas.width, canvas.height);
      const { anchor, dir } = getAnchorAndDir(raysOrigin, canvas.width, canvas.height);
      gl.uniform2fv(locs.rPos, anchor);
      gl.uniform2fv(locs.rDir, dir);
    };

    window.addEventListener('resize', resize);
    resize();

    let raf;
    const render = t => {
      smoothMouseRef.current.x += (mouseRef.current.x - smoothMouseRef.current.x) * 0.08;
      smoothMouseRef.current.y += (mouseRef.current.y - smoothMouseRef.current.y) * 0.08;

      gl.uniform1f(locs.time, t * 0.001);
      gl.uniform3fv(locs.rCol, hexToRgb(raysColor));
      gl.uniform1f(locs.rSpd, raysSpeed);
      gl.uniform1f(locs.lSpr, lightSpread);
      gl.uniform1f(locs.rLen, rayLength);
      gl.uniform1f(locs.puls, pulsating ? 1.0 : 0.0);
      gl.uniform1f(locs.fade, fadeDistance);
      gl.uniform1f(locs.sat, saturation);
      gl.uniform2f(locs.mPos, smoothMouseRef.current.x, smoothMouseRef.current.y);
      gl.uniform1f(locs.mInf, mouseInfluence);
      gl.uniform1f(locs.noise, noiseAmount);
      gl.uniform1f(locs.dist, distortion);
      gl.uniform1f(locs.intens, lightIntensity);

      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    const handleMouseMove = e => {
      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current = { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
    };
    if (followMouse) window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(raf);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      gl.deleteProgram(program);
    };
  }, [isVisible, raysOrigin, raysColor, raysSpeed, lightSpread, rayLength, pulsating, fadeDistance, saturation, followMouse, mouseInfluence, noiseAmount, distortion, lightIntensity]);

  return <div ref={containerRef} className={`light-rays-container ${className}`} />;
};

export default LightRays;